import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions } from '../redux/AlertController';

// Every driver-list filter the backend understands (see buildDriverFilters in
// carTowRequestController). Empty string = not applied; sortBy defaults to newest first.
export const EMPTY_DRIVER_FILTERS = {
    city: '',
    area: '',
    state: '',
    verificationStatus: '',
    vechicleType: '',
    vechicleSubType: '',
    subscription: '',
    debt: '',
    activityLevel: '',
    ban: '',
    payment: '',
    updateRequestStatus: '',
    minActivityPoints: '',
    maxActivityPoints: '',
    minTrips: '',
    maxTrips: '',
    minCancelled: '',
    maxCancelled: '',
    minWarnings: '',
    maxWarnings: '',
    minBalance: '',
    maxBalance: '',
    joinedFrom: '',
    joinedTo: '',
    sortBy: 'newest'
};

export const useCarTows = () => {
    const [drivers, setDrivers] = useState([]);
    const [driverRequests, setDriverRequests] = useState([]);
    const [allRequests, setAllRequests] = useState([]);
    const [requestsCount, setRequestsCount] = useState(0);
    const [updateRequests, setUpdateRequests] = useState([]);
    const [updateRequestsCount, setUpdateRequestsCount] = useState(0);
    const [locations, setLocations] = useState([]);
    const [totals, setTotals] = useState({ total: 0, active: 0, inactive: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [driverFilters, setDriverFilters] = useState(EMPTY_DRIVER_FILTERS);
    const dispatch = useDispatch();
    const user = useSelector(state => state.userController.user);

    const fetchLocations = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/locations`, {
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await response.json();
            if (!response.ok) {
                throw new Error(json.error || json.message || 'تعذر جلب المدن والمناطق');
            }
            setLocations(json.locations || []);
            return json.locations || [];
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return [];
        }
    };

    // Fetch drivers with pagination
    const fetchDrivers = async (page = 1, filters = driverFilters) => {
        const applied = { ...EMPTY_DRIVER_FILTERS, ...(filters || {}) };

        try {
            setIsLoading(true);
            const params = new URLSearchParams();
            params.append('page', page);
            Object.entries(applied).forEach(([key, value]) => {
                const text = String(value ?? '').trim();
                if (text !== '' && !(key === 'sortBy' && text === 'newest')) params.append(key, text);
            });

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/drivers?${params.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setDrivers(json.drivers);
            setTotalPages(json.pages);
            setCurrentPage(json.currentPage);
            setTotals({
                total: json.total || 0,
                active: json.activeCount || 0,
                inactive: json.inactiveCount || 0
            });
            setDriverFilters(applied);
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
        } finally {
            setIsLoading(false);
        }
    };

    const applyDriverFilters = async (filters) => {
        setSearchQuery('');
        setSearchResults(null);
        await fetchDrivers(1, filters);
    };

    const clearDriverFilters = async () => {
        setSearchQuery('');
        setSearchResults(null);
        await fetchDrivers(1, EMPTY_DRIVER_FILTERS);
    };

    // Get all requests
    const fetchAllRequests = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/requests`, {
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            const pendingRequests = json.requests.filter(req => req.state === 'pending');
            setAllRequests(json.requests);
            setRequestsCount(pendingRequests.length);
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };
    const fetchUpdateRequests = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/updates`, {
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error || 'تعذر جلب طلبات التحديث');
            }

            const pending = (json.requests || []).filter((req) => req.state === 'pending');
            setUpdateRequests(json.requests || []);
            setUpdateRequestsCount(pending.length || 0);
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    // Search drivers by name, phone number, or driver number
    const handleSearch = async (query) => {
        setSearchQuery(query);
        const trimmed = query.trim();
        if (!trimmed) {
            setSearchResults(null);
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/drivers/search?q=${encodeURIComponent(trimmed)}`, {
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setSearchResults(json);
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
        }
    };

    // Approve request
    const handleApprove = async (requestId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/requests/${requestId}/approve`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            // Update local state
            setAllRequests(prev => {
                const updated = prev.map(req => 
                    req._id === requestId ? { ...req, state: 'approved' } : req
                );
                const pendingCount = updated.filter(req => req.state === 'pending').length;
                setRequestsCount(pendingCount);
                return updated;
            });

            // Refresh drivers list
            await fetchDrivers(currentPage);

            dispatch(alertActions.showAlert({ msg: 'تم قبول الطلب بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    const updateVerificationLocation = async (requestId, city, area) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/requests/${requestId}/location`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ city, area })
            });
            const json = await response.json();
            if (!response.ok) {
                throw new Error(json.error || 'تعذر تحديث الموقع');
            }
            setAllRequests(prev => prev.map(r => r._id === requestId ? json : r));
            dispatch(alertActions.showAlert({ msg: 'تم تحديث المدينة/المنطقة للطلب', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    // Reject request
    const handleReject = async (requestId, reason) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/requests/${requestId}/reject`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ reasonForRejection: reason })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            // Update local state
            setAllRequests(prev => {
                const updated = prev.map(req => 
                    req._id === requestId ? { ...req, state: 'rejected', reasonForRejection: reason } : req
                );
                const pendingCount = updated.filter(req => req.state === 'pending').length;
                setRequestsCount(pendingCount);
                return updated;
            });

            dispatch(alertActions.showAlert({ msg: 'تم رفض الطلب بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    // Toggle driver ban status
    const toggleDriverBan = async (driverId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/drivers/${driverId}/toggle-ban`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            // Update local state
            setDrivers(prev => prev.map(driver => 
                driver._id === driverId ? { ...driver, isBanned: json.isBanned } : driver
            ));

            dispatch(alertActions.showAlert({ 
                msg: json.isBanned ? 'تم حظر السائق بنجاح' : 'تم إلغاء حظر السائق بنجاح', 
                type: 'success' 
            }));
            
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    // Reactivate deleted driver
    const reactivateDriver = async (driverId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/drivers/${driverId}/reactivate`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });

            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setDrivers(prev => prev.map(driver => 
                driver._id === driverId ? { ...driver, ...json } : driver
            ));

            if (searchResults) {
                setSearchResults(prev => prev?.map?.((driver) => driver._id === driverId ? { ...driver, ...json } : driver) || null);
            }

            dispatch(alertActions.showAlert({ msg: 'تم إعادة تفعيل الحساب بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    // Update driver warnings
    const updateWarnings = async (driverId, warnings) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/drivers/${driverId}/warnings`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ warnings })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            // Update local state
            setDrivers(prev => prev.map(driver => 
                driver._id === driverId ? { ...driver, warrnings: warnings } : driver
            ));

            dispatch(alertActions.showAlert({ msg: 'تم تحديث التحذيرات بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    const deleteDriver = async (driverId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/drivers/${driverId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error || 'تعذر حذف السائق');
            }

            setDrivers(prev => prev.filter(driver => driver._id !== driverId));
            if (searchResults) {
                setSearchResults(prev => prev?.filter?.((driver) => driver._id !== driverId) || null);
            }

            dispatch(alertActions.showAlert({ msg: 'تم حذف السائق نهائياً', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    const updateDriverNumber = async (driverId, driverNumber) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/drivers/${driverId}/number`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ driverNumber })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error || 'تعذر تحديث رقم السائق');
            }

            setDrivers(prev => prev.map(driver =>
                driver._id === driverId ? { ...driver, driverNumber: json.driverNumber } : driver
            ));

            if (searchResults) {
                setSearchResults(prev => prev?.map?.((driver) =>
                    driver._id === driverId ? { ...driver, driverNumber: json.driverNumber } : driver
                ) || null);
            }

            dispatch(alertActions.showAlert({ msg: 'تم تحديث رقم السائق بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    const updateAdminNotes = async (driverId, adminNotes) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/drivers/${driverId}/admin-notes`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ adminNotes })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error || 'تعذر تحديث الملاحظات');
            }

            setDrivers(prev => prev.map(driver =>
                driver._id === driverId ? { ...driver, adminNotes: json.adminNotes } : driver
            ));

            dispatch(alertActions.showAlert({ msg: 'تم تحديث الملاحظات بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    // recordAsProfit: count the increase as cash income on the profits page (e.g. a driver paying
    // off their debt in cash). Leave off for plain corrections.
    const updateBalance = async (driverId, balance, recordAsProfit = false) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/drivers/${driverId}/balance`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ balance, recordAsProfit })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error || 'تعذر تحديث الرصيد');
            }

            setDrivers(prev => prev.map(driver =>
                driver._id === driverId ? { ...driver, balance: json.balance } : driver
            ));

            if (searchResults) {
                setSearchResults(prev => prev?.map?.((driver) =>
                    driver._id === driverId ? { ...driver, balance: json.balance } : driver
                ) || null);
            }

            dispatch(alertActions.showAlert({
                msg: json.recordedProfit > 0
                    ? `تم تحديث الرصيد واحتساب ${json.recordedProfit} د.ل ضمن الأرباح`
                    : 'تم تحديث الرصيد بنجاح',
                type: 'success'
            }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    const handleApproveUpdate = async (requestId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/updates/${requestId}/approve`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                }
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error || 'تعذر قبول طلب التحديث');
            }

            setUpdateRequests(prev => {
                const updated = prev.map(req => req._id === requestId ? { ...req, state: 'approved' } : req);
                const pending = updated.filter(req => req.state === 'pending').length;
                setUpdateRequestsCount(pending);
                return updated;
            });

            await fetchDrivers(currentPage);
            dispatch(alertActions.showAlert({ msg: 'تم قبول طلب التحديث', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    const updateDriverUpdateLocation = async (requestId, city, area) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/updates/${requestId}/location`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ city, area })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error || 'تعذر تحديث الموقع لطلب التحديث');
            }

            setUpdateRequests(prev => prev.map(req => req._id === requestId ? json : req));
            dispatch(alertActions.showAlert({ msg: 'تم تحديث المدينة/المنطقة لطلب التحديث', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    const handleRejectUpdate = async (requestId, reason) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/updates/${requestId}/reject`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ reasonForRejection: reason })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error || 'تعذر رفض طلب التحديث');
            }

            setUpdateRequests(prev => {
                const updated = prev.map(req => req._id === requestId ? { ...req, state: 'rejected', reasonForRejection: reason } : req);
                const pending = updated.filter(req => req.state === 'pending').length;
                setUpdateRequestsCount(pending);
                return updated;
            });

            dispatch(alertActions.showAlert({ msg: 'تم رفض طلب التحديث', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    const updatePaymentMethods = async (driverId, { doesAcceptCard, doesAcceptTransfer }) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/drivers/${driverId}/payment-methods`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ doesAcceptCard, doesAcceptTransfer })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error || 'تعذر تحديث طرق الدفع');
            }

            setDrivers(prev => prev.map(driver =>
                driver._id === driverId ? { ...driver, doesAcceptCard: json.doesAcceptCard, doesAcceptTransfer: json.doesAcceptTransfer } : driver
            ));

            dispatch(alertActions.showAlert({ msg: 'تم تحديث طرق الدفع بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    const updateCancellationLog = async (driverId, cancellationLog) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/drivers/${driverId}/cancellation-log`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ cancellationLog })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error || 'تعذر تحديث سجل الإلغاءات');
            }

            setDrivers(prev => prev.map(driver =>
                driver._id === driverId ? { ...driver, cancellationLog: json.cancellationLog, tempBanUntil: json.tempBanUntil } : driver
            ));

            dispatch(alertActions.showAlert({ msg: 'تم تحديث سجل الإلغاءات بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    // Temporary ban (with the reason the driver sees) / lift, from the driver details.
    const setDriverTempBan = async (driverId, { days, hours, reason }) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/drivers/${driverId}/temp-ban`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${user?.token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ days, hours, reason })
            });
            const json = await response.json();
            if (!response.ok) throw new Error(json.error || 'تعذر إيقاف السائق');
            const patch = { tempBanUntil: json.tempBanUntil, tempBanReason: json.tempBanReason, tempBanBy: json.tempBanBy, tempBanAdmin: json.tempBanAdmin };
            setDrivers(prev => prev.map(driver => (driver._id === driverId ? { ...driver, ...patch } : driver)));
            dispatch(alertActions.showAlert({ msg: 'تم إيقاف السائق مؤقتاً', type: 'success' }));
            return patch;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    const liftDriverTempBan = async (driverId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/car-tows/drivers/${driverId}/temp-ban/lift`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${user?.token}` }
            });
            const json = await response.json();
            if (!response.ok) throw new Error(json.error || 'تعذر رفع الإيقاف');
            const patch = { tempBanUntil: null, tempBanReason: '', tempBanBy: null, tempBanAdmin: '' };
            setDrivers(prev => prev.map(driver => (driver._id === driverId ? { ...driver, ...patch } : driver)));
            dispatch(alertActions.showAlert({ msg: 'تم رفع الإيقاف', type: 'success' }));
            return patch;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    const addCityWithArea = async (city, area) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/locations`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ city, areas: area ? [area] : [] })
            });
            const json = await response.json();
            if (!response.ok) {
                throw new Error(json.error || json.message || 'تعذر إضافة المدينة');
            }
            await fetchLocations();
            dispatch(alertActions.showAlert({ msg: 'تمت إضافة المدينة بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    const addAreaToCity = async (cityName, area) => {
        try {
            const match = (locations || []).find((loc) => loc.city?.toLowerCase() === (cityName || '').toLowerCase());
            if (!match?._id) {
                throw new Error('المدينة غير موجودة في القائمة');
            }
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/locations/${match._id}/areas`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ area })
            });
            const json = await response.json();
            if (!response.ok) {
                throw new Error(json.error || json.message || 'تعذر إضافة المنطقة');
            }
            await fetchLocations();
            dispatch(alertActions.showAlert({ msg: 'تمت إضافة المنطقة بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    // Initial fetch
    useEffect(() => {
        if (user?.token) {
            fetchDrivers();
            fetchAllRequests();
            fetchUpdateRequests();
            fetchLocations();
        }
    }, [user]);

    return {
        setDriverTempBan,
        liftDriverTempBan,
        drivers: searchQuery ? searchResults : drivers,
        driverRequests,
        allRequests,
        requestsCount,
        updateRequests,
        updateRequestsCount,
        locations,
        isLoading,
        currentPage,
        totalPages,
        totals,
        driverFilters,
        searchQuery,
        handleSearch,
        handleApprove,
        handleReject,
        handleApproveUpdate,
        handleRejectUpdate,
        updateVerificationLocation,
        updateDriverUpdateLocation,
        addCityWithArea,
        addAreaToCity,
        fetchDrivers,
        applyDriverFilters,
        clearDriverFilters,
        fetchAllRequests,
        fetchUpdateRequests,
        toggleDriverBan,
        updateWarnings,
        reactivateDriver,
        deleteDriver,
        updateDriverNumber,
        updatePaymentMethods,
        updateCancellationLog,
        updateAdminNotes,
        updateBalance
    };
};